



























































































































import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import StateButtonComponent, {
  ButtonState,
} from "@/components/common/StateButton.vue";
import { ComBox } from "@/data/models/ComBox";
import { hasCapability, DeviceCapability } from "@/data/models/DeviceCapability";
import {
  getHyDryEventCategoryDescription,
  getHyDryEventNumber,
  getHyDryPanelEventNumber,
  HyDryEvent,
  HyDryPanelEvent,
  HyDryEventCategory,
  isEventNumberOfHyDryLidEvent,
  isEventNumberOfHyDryPauseEvent,
  isEventNumberOfSensorEvent,
  getDefaultSelectedHyDryEventCategories,
  isHyDryEventLogAllowedByCapabilities,
} from "@/data/models/HyDryEvent";
import { HyDryEventLog } from "@/data/models/HyDryEventLog";
import {
  getIresDeviceCategory,
  IresDevice,
  IresDeviceCategory,
} from "@/data/models/IresDevice";
import Panel from "@/data/models/Panel";
import { ProgramChain } from "@/data/models/ProgramChain";
import {
  observeHyDryEventLogsOfProgramChain,
  compareHyDryEventLogs,
} from "@/data/repository/HyDryLogsRepository";
import { 
  seeLidAndPauseEventLogsAccess,
  seeAllEventLogsAccess,
  toggleStepEventLogsAccess,
  seeSensorEventLogsAccess
} from "@/permissions/UserPermissions";
import { generateRandomUuid } from "@/utils/Utils";
import { firestore } from "firebase";
import { Vue, Component, Prop, Ref, Watch } from "vue-property-decorator";
import HyDryEventLogDetailsComponent from "./HyDryEventLogDetails.vue";
import HyDryEventLogItemComponent from "./HyDryEventLogItem.vue";
import { StateChanger } from "vue-infinite-loading";
import { ClientModule } from "@/store/modules/ClientModule";
import { getEffectiveUser } from "@/utils/ViewAs";

interface ProgramChainTimeRange {
  startDate: Date;
  endDate: Date | null;
  chainNumber: number;
}

interface LogData {
  hyDryEventLog: HyDryEventLog;
  chainNumber: number;
}

class HyDryEventLogFilter {
  private categoriesToInclude: Set<HyDryEventCategory>;

  private showLidEvents: boolean;
  private showPauseEvents: boolean;
  private showSensorEvents: boolean;

  private onlyShowNotes: boolean;

  constructor(
    categoriesToInclude: HyDryEventCategory[],
    onlyShowNotes: boolean
  ) {
    this.categoriesToInclude = new Set<HyDryEventCategory>();
    categoriesToInclude.forEach((category) => {
      this.categoriesToInclude.add(category);
    });

    this.showLidEvents = this.categoriesToInclude.has(HyDryEventCategory.LID);
    this.showPauseEvents = this.categoriesToInclude.has(
      HyDryEventCategory.PAUSE
    );
    this.showSensorEvents = this.categoriesToInclude.has(
      HyDryEventCategory.SENSOR
    );

    this.onlyShowNotes = onlyShowNotes;
  }

  doesLogMatchFilter(
    hyDryEventLog: HyDryEventLog,
    isAllowedToSeeAllEventLogs: boolean,
    displayFirmwareVersion: string | null,
    isPanel: boolean,
    isAllowedToSeeLidAndPauseEventLogs: boolean,
    showStepEvents?: boolean,
  ) {
    if (!isPanel) {
      const startOf2023 = 
        (new Date("2023/01/01")).valueOf();

      const dryRunStart =
        hyDryEventLog.dryRunStartDate.toMillis();

      const didRunStartInOrAfter2023 = 
        startOf2023 <= dryRunStart;

      const requiredDisplayFirwareVersion = "1.11.65"

      const doesMeetDisplayFirmwareRequirements =
        displayFirmwareVersion &&
        requiredDisplayFirwareVersion <= displayFirmwareVersion;

      if (!isAllowedToSeeAllEventLogs) {
        if (!didRunStartInOrAfter2023 && !doesMeetDisplayFirmwareRequirements) {
          return false;
        }
      }

      if (this.onlyShowNotes && !hyDryEventLog.note) {
        return false;
      }

      if (hyDryEventLog.note) {
        return true;
      }

      // Capability gate first: a missing capability hides the log whatever the
      // toggle buttons below say.
      if (
        !this.isLogAllowedByCapabilities(
          hyDryEventLog,
          isPanel,
          isAllowedToSeeAllEventLogs,
          isAllowedToSeeLidAndPauseEventLogs
        )
      ) {
        return false;
      }

      if (isEventNumberOfHyDryLidEvent(hyDryEventLog.eventNumber)) {
        if (!this.categoriesToInclude.has(HyDryEventCategory.ORGANISATION)) {
          return false;
        }

        return this.showLidEvents;
      }

      if (isEventNumberOfHyDryPauseEvent(hyDryEventLog.eventNumber)) {
        if (!this.categoriesToInclude.has(HyDryEventCategory.ORGANISATION)) {
          return false;
        }

        return this.showPauseEvents;
      }

      if (isEventNumberOfSensorEvent(hyDryEventLog.eventNumber)) {
        if (hyDryEventLog.additionalData) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [typeStr, _] = hyDryEventLog.additionalData.split(";");
          const type = Number(typeStr);

          if (type === 10 && !isAllowedToSeeAllEventLogs) {
            return false;
          }
        }

        return this.showSensorEvents;
      }
    }
    else {
      const logsStartingFrom = 
        (new Date("2023/08/05")).valueOf();

      const createdAt =
        hyDryEventLog.createdAt.toMillis();

      const validLog = 
        logsStartingFrom <= createdAt;

      // Panel logs older than the cut-off were written before the log vocabulary settled, so they
      // are not shown to clients. IRES keeps them: nothing is ever hidden from an IRES user.
      if (!validLog && !isAllowedToSeeAllEventLogs) {
        return false;
      }

      if (!showStepEvents && getHyDryPanelEventNumber(HyDryPanelEvent.STEP) === hyDryEventLog.eventNumber) {
        return false;
      }

      if (this.onlyShowNotes && !hyDryEventLog.note) {
        return false;
      }

      if (hyDryEventLog.note) {
        return true;
      }

      if (hyDryEventLog.eventCategory === HyDryEventCategory.IRES) {
        if (!isAllowedToSeeAllEventLogs) {
          return false;
        }
      }

      if (
        !this.isLogAllowedByCapabilities(
          hyDryEventLog,
          isPanel,
          isAllowedToSeeAllEventLogs,
          isAllowedToSeeLidAndPauseEventLogs
        )
      ) {
        return false;
      }
    }

    return this.categoriesToInclude.has(hyDryEventLog.eventCategory);
  }

  /**
   * The capability gate, applied to both device types. `eventCategory` is the effective category
   * (`HyDryLogsRepository.convertSnapshots` already rewrote LID / PAUSE / SENSOR), which the ComBox
   * warning rules depend on.
   */
  private isLogAllowedByCapabilities(
    hyDryEventLog: HyDryEventLog,
    isPanel: boolean,
    isAllowedToSeeAllEventLogs: boolean,
    isAllowedToSeeLidAndPauseEventLogs: boolean
  ) {
    return isHyDryEventLogAllowedByCapabilities(
      {
        eventNumber: hyDryEventLog.eventNumber,
        eventCategory: hyDryEventLog.eventCategory,
        isPanel: isPanel,
      },
      {
        canSeeAllEventLogs: isAllowedToSeeAllEventLogs,
        canSeeLidAndPauseEventLogs: isAllowedToSeeLidAndPauseEventLogs,
      }
    );
  }
}

@Component({
  components: {
    "log-item": HyDryEventLogItemComponent,
    "log-details": HyDryEventLogDetailsComponent,
    "state-button": StateButtonComponent,
  },
})
export default class HyDryEventLogListComponent extends Vue {
  @Prop()
  iresDevice!: IresDevice;

  @Prop()
  programChain!: ProgramChain;

  @Prop({ default: () => new Map<number, number>() })
  @Prop({ default: false })
  showRelativeChainNumber!: boolean;

  @Prop()
  isComBox!: boolean;

  @Prop({ default: () => new Map<number, number>() })
  programChainNumberLookup!: Map<number, number>;
  @Prop() doesSupportExtendedSensors!: boolean;

  runStartTimestampToChainNumber = new Map<number, number>();

  @Ref("logDetails")
  hyDryEventLogDetailsComponent!: HyDryEventLogDetailsComponent;

  observeHyDryEventLogsInTimeRange: Array<() => void> = [];
  programChainTimeRanges: ProgramChainTimeRange[] = [];

  doesLogsExist = true;

  isLoading = true;
  infiniteId = new Date();
  pageSize = 15;
  sliceLength = this.pageSize;

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToViewLidAndPauseEventLogs() {
    return seeLidAndPauseEventLogsAccess(this.activeUser);
  }

  get isAllowedToViewSensorEventLogs() {
    return seeSensorEventLogsAccess(this.activeUser);
  }

  get isAllowedToSeeAllEventLogs() {
    return seeAllEventLogsAccess(this.activeUser);
  }

  get isAllowedToToggleStepEventLogs() {
    return toggleStepEventLogsAccess(this.activeUser);
  }

  get isPanel() {
    return getIresDeviceCategory(this.iresDevice) === IresDeviceCategory.PANEL;
  }

  get slicedLogsToDisplay() {
    return this.sliceLogsToDisplay(this.sliceLength);
  }

  get showInfiniteLoading() {
    return this.slicedLogsToDisplay.length > 0;
  }

  sliceLogsToDisplay(length: number) {
    //this.hasNoClientPanels = true;
    const logs = this.logsToDisplay
    const slicedLogs = logs.slice(0, length) as LogData[];

    return slicedLogs;
  }

  async infiniteHandler(data: StateChanger) {
    const nextSliceLength = this.sliceLength + this.pageSize;
    const nextSlice = this.sliceLogsToDisplay(nextSliceLength);

    this.sliceLength = nextSliceLength;

    if (nextSliceLength > nextSlice.length) {
      data.complete();
    } else {
      data.loaded();
    }
  }

  allHyDryEventCategories = [
    HyDryEventCategory.ORGANISATION,
    HyDryEventCategory.DRYING_PROGRESS,
    HyDryEventCategory.ALERT,
    HyDryEventCategory.IRES,
    HyDryEventCategory.OTHER,

    HyDryEventCategory.NOTE,
    HyDryEventCategory.LID,
    HyDryEventCategory.PAUSE,
    HyDryEventCategory.SENSOR,
  ];

  dependentHyDryEventCategories = new Map([
    [
      HyDryEventCategory.ORGANISATION,
      this.isAllowedToViewLidAndPauseEventLogs
        ? [HyDryEventCategory.LID, HyDryEventCategory.PAUSE]
        : [],
    ],
  ]);

  onlyShowNotes = false;
  sortDescending = true;

  /**
   * The toggle buttons above the log list. A category only gets a button if the
   * user is allowed to see that category at all, so a client is never offered a
   * filter for logs the filter would drop anyway.
   */
  get selectableHyDryEventCategories() {
    const categories: HyDryEventCategory[] = [];

    if (this.isAllowedToViewLidAndPauseEventLogs) {
      categories.push(HyDryEventCategory.PAUSE, HyDryEventCategory.LID);
    }

    if (this.isAllowedToViewSensorEventLogs) {
      categories.push(HyDryEventCategory.SENSOR);
    }

    return categories;
  }

  showStepEvents = false;

  /**
   * A copy, never `allHyDryEventCategories` itself: `enableCategory` pushes into this array and
   * would otherwise grow the master list too.
   */
  selectedHyDryEventCategories = getDefaultSelectedHyDryEventCategories(
    this.allHyDryEventCategories
  );

  subscriptions: Array<() => void> = [];

  hyDryEventLogs: LogData[] = [];
  hyDryLidEventLogs: LogData[] = [];

  sortedHyDryEventLogs: LogData[] = [];

  logsToDisplay: LogData[] = [];

  @Watch("programChain", { immediate: true })
  onProgramChainChanged(after: ProgramChain, before: ProgramChain | undefined) {
    if (
      before?.id !== after.id ||
      after.runs[after.runs.length - 1].id !==
        before.runs[before.runs.length - 1].id
    ) {
      this.isLoading = true;
      this.unsubscribe();

      this.runStartTimestampToChainNumber.clear();

      after.runs.forEach((run) => {
        this.runStartTimestampToChainNumber.set(
          run.startDate.toMillis(),
          run.chainNumber
        );
      });

      this.hyDryEventLogs = [];
      this.hyDryLidEventLogs = [];

      const eventCategoriesToObserve = [
        HyDryEventCategory.ORGANISATION,
        HyDryEventCategory.DRYING_PROGRESS,
        HyDryEventCategory.ALERT,
        HyDryEventCategory.OTHER,
        HyDryEventCategory.IRES,

        HyDryEventCategory.NOTE,
        HyDryEventCategory.PAUSE,
        HyDryEventCategory.SENSOR,
      ];

      this.subscriptions.push(
        observeHyDryEventLogsOfProgramChain(
          after,
          10000,
          eventCategoriesToObserve,
          (logs: HyDryEventLog[]) => {
            this.hyDryEventLogs = logs.map((log) => {
              this.isLoading = false;

              return {
                hyDryEventLog: log,
                chainNumber:
                  this.runStartTimestampToChainNumber.get(
                    log.dryRunStartDate.toMillis()
                  ) ?? -1,
              };
            });

            this.calculateSortedHyDryEventLogs();
          },
          this.isPanel
        )
      );

      // Lid logs live in their own category, so a user who may not see them has
      // no reason to fetch them either.
      if (this.isAllowedToViewLidAndPauseEventLogs) {
        this.subscriptions.push(
          observeHyDryEventLogsOfProgramChain(
            after,
            100,
            [HyDryEventCategory.LID],
            (logs: HyDryEventLog[]) => {
              this.hyDryLidEventLogs = logs.map((log) => {
                this.isLoading = false;

                return {
                  hyDryEventLog: log,
                  chainNumber:
                    this.runStartTimestampToChainNumber.get(
                      log.dryRunStartDate.toMillis()
                    ) ?? -1,
                };
              });

              this.calculateSortedHyDryEventLogs();
            },
            this.isPanel
          )
        );
      }
    }
  }

  hyDryEventLogFilter = new HyDryEventLogFilter(
    this.selectedHyDryEventCategories,
    this.onlyShowNotes
  );

  showDivider(logIndex: number) {
    if (this.runStartTimestampToChainNumber.size <= 1) {
      return false;
    }

    const logBefore = this.logsToDisplay[logIndex - 1];
    const logAfter = this.logsToDisplay[logIndex];

    return logBefore?.chainNumber !== logAfter?.chainNumber;
  }

  getDividerText(logIndex: number) {
    const chainNumber = this.logsToDisplay[logIndex].chainNumber;
    const absoluteChainNumber = chainNumber;

    const relativeChainNumber = this.programChainNumberLookup.get(chainNumber);

    if (absoluteChainNumber == -1) {
      return "";
    }

    if (this.showRelativeChainNumber) {
      return this.$t("dryingInfo.formatRelativeAndAbsoluteChainNumber", [
        relativeChainNumber,
        absoluteChainNumber,
      ]).toString();
    }

    return this.$t("panelDetail.dryingProgressGraphControl.formatDrying", [
      absoluteChainNumber,
    ]);
  }

  /**
   * Updates the chain numbers for panel logs to handle cases where logs with event number 1 (Panel eingeschaltet)
   * cannot be directly mapped to a program chain at the time of generation due to missing information about whether 
   * the program chain has ended. This issue results in an incorrect dry run start date. To address this, the method 
   * iterates over the logs and manually shifts the log to the next chain.
   *
   * @remarks
   * - This is a temporary solution implemented on the frontend until a better approach is identified.
   * - The method ensures that logs with event number 1 are assigned the chain number of the previous log 
   *   if the following conditions are met:
   *   1. The next log exists.
   *   2. The next log's event number is 1 (Panel eingeschaltet).
   *   3. The next log's chain number differs from the current log's chain number.
   *   4. The current log's dryRunStartDate is earlier than or equal to the next log's eventDate.
   */
  updateChainNumbersForPanelLogs(): void {
    for (let i = 0; i < this.sortedHyDryEventLogs.length - 1; i++) {
      const currentLog = this.sortedHyDryEventLogs[i];
      const nextLog = this.sortedHyDryEventLogs[i + 1];

      if (
        nextLog &&
        (
          nextLog.hyDryEventLog.eventNumber === 1 ||
          nextLog.hyDryEventLog.eventNumber === 5
        ) &&
        nextLog.chainNumber !== currentLog.chainNumber &&
        currentLog.hyDryEventLog.dryRunStartDate <= nextLog.hyDryEventLog.eventDate
      ) {
        nextLog.chainNumber = currentLog.chainNumber;
      }
    }
  }

  calculateSortedHyDryEventLogs() {
    this.sortedHyDryEventLogs = [
      ...this.hyDryEventLogs,
      ...this.hyDryLidEventLogs,
    ].sort((a, b) => compareHyDryEventLogs(a.hyDryEventLog, b.hyDryEventLog));

    if (this.isPanel) {
      this.updateChainNumbersForPanelLogs();
    }

    this.calculateLogsToDisplay();
  }

  calculateLogsToDisplay() {
    const comBox = this.iresDevice as ComBox;
    const hasDisplay = !this.isPanel && hasCapability(comBox, DeviceCapability.HAS_DISPLAY);
    const displayFirmwareVersion = hasDisplay ? (comBox.displayFirmwareVersion ?? null) : null;

    const filteredLogs = this.sortedHyDryEventLogs.filter((log) =>
      this.hyDryEventLogFilter.doesLogMatchFilter(
        log.hyDryEventLog,
        this.isAllowedToSeeAllEventLogs,
        displayFirmwareVersion,
        this.isPanel,
        this.isAllowedToViewLidAndPauseEventLogs,
        this.isPanel ? this.showStepEvents : undefined,
      )
    );

    if (filteredLogs.length === 0) {
      this.doesLogsExist = false;
      this.isLoading = false;
    }
    else if (!this.doesLogsExist) {
      this.doesLogsExist = true;
    }

    this.logsToDisplay = this.sortDescending
      ? filteredLogs
      : filteredLogs.reverse();

    this.resetDisplayedLogs();
  }

  getHyDryEventCategoryButtonText(category: HyDryEventCategory) {
    return getHyDryEventCategoryDescription(this, category, false);
  }

  toggleOnlyShowNotes() {
    this.onlyShowNotes = !this.onlyShowNotes;
    this.hyDryEventLogFilter = this.createHyDryEventLogFilter();
  }

  getCategoryButtonState(category: HyDryEventCategory) {
    if (category === HyDryEventCategory.NOTE) {
      return this.onlyShowNotes ? ButtonState.YELLOW : ButtonState.GREEN;
    }

    return this.selectedHyDryEventCategories.includes(category)
      ? ButtonState.GREEN
      : ButtonState.RED;
  }

  toggleStepEventsButtonState(val: boolean) {
    if (val) {
      return ButtonState.GREEN;
    }

    return ButtonState.RED;
  }

  toggleStepEventsToDisplay(val: boolean) {
    this.showStepEvents = val;

    this.calculateLogsToDisplay();
  }

  toggleEventCategoryToDisplay(category: HyDryEventCategory) {
    if (category === HyDryEventCategory.NOTE) {
      this.toggleOnlyShowNotes();
      return;
    }

    const dependentCategories = this.dependentHyDryEventCategories.get(
      category
    );

    if (this.isCategoryEnabled(category)) {
      this.disableCategory(category);

      dependentCategories?.forEach((dependentCategory) => {
        if (this.isCategoryEnabled(dependentCategory)) {
          this.disableCategory(dependentCategory);
        }
      });
    } else {
      this.enableCategory(category);

      dependentCategories?.forEach((dependentCategory) => {
        if (!this.isCategoryEnabled(dependentCategory)) {
          this.enableCategory(dependentCategory);
        }
      });
    }

    this.hyDryEventLogFilter = this.createHyDryEventLogFilter();
  }

  isCategoryEnabled(category: HyDryEventCategory) {
    return this.selectedHyDryEventCategories.includes(category);
  }

  enableCategory(category: HyDryEventCategory) {
    this.selectedHyDryEventCategories.push(category);
  }

  disableCategory(category: HyDryEventCategory) {
    this.selectedHyDryEventCategories = this.selectedHyDryEventCategories.filter(
      (currentlyDisplayedCategory) => category !== currentlyDisplayedCategory
    );
  }

  get toggleSortOrderButtonState() {
    return ButtonState.RED;
  }

  get toggleSortOrderIcon() {
    return this.sortDescending ? "mdi-arrow-up" : "mdi-arrow-down";
  }

  toggleSortOrder() {
    this.sortDescending = !this.sortDescending;
    this.calculateLogsToDisplay();
  }

  @Watch("hyDryEventLogFilter")
  onFilterChanged() {
    this.calculateLogsToDisplay();
  }

  @Watch("ignoreIresAdminOrIresClientCheck")
  onIgnoreIresAdminOrIresClientCheckChanged() {
    this.calculateLogsToDisplay();
  }

  get ignoreIresAdminOrIresClientCheck() {
    return ClientModule.ignoreIresAdminOrIresClientCheck;
  }

  resetDisplayedLogs() {
    this.sliceLength = this.pageSize;
    this.infiniteId = new Date();
  }

  doesProgramChainTimeRangeIncludeTimestamp(
    timeRange: ProgramChainTimeRange,
    timestamp: number
  ) {
    if (timestamp < timeRange.startDate.getTime()) {
      return false;
    }

    return true;
  }

  unsubscribe() {
    this.subscriptions.forEach((subscription) => subscription());
    this.subscriptions = [];
  }

  beforeDestroy() {
    this.unsubscribe();
  }

  private createHyDryEventLogFilter() {
    return new HyDryEventLogFilter(
      this.selectedHyDryEventCategories,
      this.onlyShowNotes
    );
  }

  onOpenLogDetails(log: HyDryEventLog) {
    this.hyDryEventLogDetailsComponent.show(log);
  }

  /** Phone layout (below 600 px). */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** Notes are ComBox-only, and never while "Sicht auf" is active (ticket 2610051). */
  get showCreateLogButton() {
    return getIresDeviceCategory(this.iresDevice) === IresDeviceCategory.COMBOX && !isViewAsSelected();
  }

  onCreateNoteLog() {
    const iresDeviceCategory = getIresDeviceCategory(this.iresDevice);

    if (iresDeviceCategory === IresDeviceCategory.PANEL) {
      const panel = this.iresDevice as Panel;
      const now = firestore.Timestamp.now();

      if (panel.panelStatus) {
        const log: HyDryEventLog = {
          id: generateRandomUuid(),
          eventNumber: getHyDryPanelEventNumber(HyDryPanelEvent.NOTE),
          eventCategory: HyDryEventCategory.NOTE,
          eventDate: now,

          createdAt: now,

          dryRunStartDate: now,
          dryRunActive: false,
          program: panel.panelStatus.activeProgram,
          step: panel.panelStatus.currentStep,
          cycle: panel.panelStatus.currentCycle,
        };

        this.hyDryEventLogDetailsComponent.show(log);
      }
    }
    else if (iresDeviceCategory === IresDeviceCategory.COMBOX) {
      const comBox = this.iresDevice as ComBox;
      const now = firestore.Timestamp.now();

      const log: HyDryEventLog = {
        id: generateRandomUuid(),
        eventNumber: getHyDryEventNumber(HyDryEvent.NOTE),
        eventCategory: HyDryEventCategory.NOTE,
        eventDate: now,

        createdAt: now,

        dryRunStartDate: comBox.comBoxStatus.dryRunStartDate,
        dryRunActive: comBox.comBoxStatus.dryRunActive,
        program: comBox.comBoxStatus.activeProgram,
        step: comBox.comBoxStatus.step,
        cycle: comBox.comBoxStatus.cycle,
      };

      this.hyDryEventLogDetailsComponent.show(log);
    }

    return;
  }
}
